import type { components } from "@/api/contract";

export type Conversation = components["schemas"]["Conversation"];

export interface ConversationGroup {
  /** Stable group key (for React keys + tests). */
  key: "favorites" | "today" | "yesterday" | "last7" | "last30" | "older";
  /** i18n key for the group caps label. */
  labelKey: string;
  items: Conversation[];
}

const GROUP_LABELS: Record<ConversationGroup["key"], string> = {
  favorites: "conversations.favorites",
  today: "conversations.today",
  yesterday: "conversations.yesterday",
  last7: "conversations.last7Days",
  last30: "conversations.last30Days",
  older: "conversations.older",
};

const ORDER: ConversationGroup["key"][] = [
  "favorites",
  "today",
  "yesterday",
  "last7",
  "last30",
  "older",
];

const DAY_MS = 86_400_000;

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Which time bucket a non-favorite conversation falls into, by `updated_at`. */
function timeBucket(updatedAt: string, today0: number): ConversationGroup["key"] {
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t) || t >= today0) return "today";
  if (t >= today0 - DAY_MS) return "yesterday";
  if (t >= today0 - 7 * DAY_MS) return "last7";
  if (t >= today0 - 30 * DAY_MS) return "last30";
  return "older";
}

/**
 * Bucket conversations for the sidebar: favorites pinned first (by their sort
 * order), then the rest by recency into Today / Yesterday / Last 7 / Last 30 /
 * Older. Favorites are excluded from the time buckets. Pure — `now` is injected
 * so it's deterministic to test.
 */
export function groupConversations(items: Conversation[], now: number): ConversationGroup[] {
  const today0 = startOfDay(now);
  const buckets = new Map<ConversationGroup["key"], Conversation[]>();

  const favorites = items
    .filter((c) => c.is_favorite)
    .sort(
      (a, b) =>
        (a.favorite_sort_order ?? 0) - (b.favorite_sort_order ?? 0) ||
        Date.parse(b.updated_at) - Date.parse(a.updated_at),
    );
  if (favorites.length) buckets.set("favorites", favorites);

  const rest = items
    .filter((c) => !c.is_favorite)
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at));
  for (const conv of rest) {
    const key = timeBucket(conv.updated_at, today0);
    const list = buckets.get(key);
    if (list) list.push(conv);
    else buckets.set(key, [conv]);
  }

  return ORDER.filter((key) => buckets.has(key)).map((key) => ({
    key,
    labelKey: GROUP_LABELS[key],
    items: buckets.get(key) ?? [],
  }));
}
